import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { DataTable } from '@/components/data-table/data-table';
import { QueryErrorBanner } from '@/components/feedback/query-error';
import { CoinCell } from '@/components/ui/coin-cell';
import { KINEXYS_ACCOUNTS_QUERY_KEY, useKinexysAccounts } from '@/hooks/queries/treasury/use-kinexys-accounts';
import { getErrorMessage } from '@/lib/api/error';
import { type KinexysAccount, updateKinexysAccount } from '@/services/api/kinexys';
import { formatCurrency, formatDate } from '@/utils/format';

const COIN_ICON_SIZE = 22;

type YesNo = 'yes' | 'no';

const YES_NO_OPTIONS: Array<{ value: YesNo; label: string }> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

interface EditDialogProps {
  account: KinexysAccount;
  onClose: () => void;
}

/** Shared PATCH → invalidate → toast flow for both dialogs. */
const useSaveAccount = (onClose: () => void) => {
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);

  const save = async (accountId: string, update: Parameters<typeof updateKinexysAccount>[1]) => {
    setIsSaving(true);
    try {
      await updateKinexysAccount(accountId, update);
      await queryClient.invalidateQueries({ queryKey: KINEXYS_ACCOUNTS_QUERY_KEY });
      toast.success('Account updated');
      onClose();
    } catch (error) {
      // Deliberate fix: the legacy page swallowed PATCH failures silently.
      toast.error(getErrorMessage(error));
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
};

const AutoUpdateBalanceDialog = ({ account, onClose }: EditDialogProps) => {
  const [enabled, setEnabled] = useState<YesNo>(account.auto_update_balance_enabled ? 'yes' : 'no');
  const { save, isSaving } = useSaveAccount(onClose);

  return (
    <Dialog onOpenChange={(open) => (open ? undefined : onClose())} open>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Auto Update Balance</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-2">
          <Label>Auto update balance</Label>
          <Select onValueChange={(value) => setEnabled(value as YesNo)} value={enabled}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {YES_NO_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button disabled={isSaving} onClick={onClose} type="button" variant="outline">
            Cancel
          </Button>
          <Button disabled={isSaving} onClick={() => save(account.id, { auto_update_balance_enabled: enabled === 'yes' })} type="button" variant="primary">
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
AutoUpdateBalanceDialog.displayName = 'AutoUpdateBalanceDialog';

interface AccountCellProps {
  account: KinexysAccount;
  onEdit: (account: KinexysAccount) => void;
}

const BalanceAvailableCell = ({ account, onEdit }: AccountCellProps) => (
  <div className="flex flex-col gap-0.5">
    <span className="font-semibold tabular-nums">{account.balance_available === null ? 'n.a.' : formatCurrency(account.balance_available, account.currency)}</span>
    {account.balance_updated_at !== null ? <span className="text-xs text-muted-foreground">updated at {formatDate(account.balance_updated_at)}</span> : null}
    <span className="text-sm">
      Auto update:{' '}
      <Button className="h-auto p-0 align-baseline text-sm" onClick={() => onEdit(account)} type="button" variant="link">
        {account.auto_update_balance_enabled ? 'YES' : 'NO'}
      </Button>
    </span>
  </div>
);
BalanceAvailableCell.displayName = 'BalanceAvailableCell';

interface ColumnHandlers {
  onEditAutoUpdate: (account: KinexysAccount) => void;
}

const buildColumns = ({ onEditAutoUpdate }: ColumnHandlers): ColumnDef<KinexysAccount, unknown>[] => [
  {
    accessorKey: 'currency',
    header: 'Currency',
    cell: ({ row }) => <CoinCell asset={row.original.currency} size={COIN_ICON_SIZE} />,
  },
  {
    accessorKey: 'id',
    header: 'BDA ID',
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        {row.original.id} - (BIC: {row.original.bic})
      </span>
    ),
  },
  {
    accessorKey: 'name',
    header: 'Name',
  },
  {
    accessorKey: 'dda_id',
    header: 'Corresponding DDA ID',
    cell: ({ row }) => (
      <span className="font-mono text-xs">
        {row.original.dda_id} - (BIC: {row.original.dda_bic})
      </span>
    ),
  },
  {
    accessorKey: 'balance_available',
    header: 'BDA Balance Available',
    cell: ({ row }) => <BalanceAvailableCell account={row.original} onEdit={onEditAutoUpdate} />,
  },
];

const KinexysAccountsPage = () => {
  const { data, isPending, isError, error, refetch } = useKinexysAccounts();
  const [autoUpdateAccount, setAutoUpdateAccount] = useState<KinexysAccount | null>(null);

  const columns = useMemo(() => buildColumns({ onEditAutoUpdate: setAutoUpdateAccount }), []);

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-3xl font-semibold">JPM Kinexys BDA accounts</h1>

      {isError ? (
        <QueryErrorBanner message={`Failed to fetch accounts: ${getErrorMessage(error)}`} onRetry={() => refetch()} />
      ) : (
        // The table stays mounted during saves/refetches — the legacy page
        // replaced it with a full-page spinner on every update (deliberate fix).
        <DataTable columns={columns} data={data} emptyMessage="No accounts" getRowId={(row) => row.id} isLoading={isPending} />
      )}

      {autoUpdateAccount ? <AutoUpdateBalanceDialog account={autoUpdateAccount} onClose={() => setAutoUpdateAccount(null)} /> : null}
    </div>
  );
};
KinexysAccountsPage.displayName = 'KinexysAccountsPage';

export default KinexysAccountsPage;
